import Ajv2020 from 'ajv/dist/2020';
import type { ErrorObject } from 'ajv';

import schemaDefinition from './CATALOGUE_TEMPLATE_SCHEMA.json';
import { getBlockRegistryForDesign } from '../blocks';

type JsonRecord = Record<string, unknown>;

export type CatalogueTemplateRegion =
  | 'hero'
  | 'header'
  | 'panel'
  | 'card'
  | 'media'
  | 'content'
  | 'footer'
  | 'aside'
  | 'toolbar'
  | (string & {});

export interface TemplateMetadata {
  label?: string;
  description?: string;
  previewImageUrl?: string;
  accentColor?: string;
  surfaceColor?: string;
}

export interface GridConfig {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  xxl?: number;
  gutter?: number | [number, number];
}

export interface LayoutOverrides {
  className?: string;
  style?: Record<string, string | number>;
  tokens?: Record<string, string>;
  grid?: GridConfig;
  ratio?: string;
  fullWidth?: boolean;
}

export type ConditionExpression =
  | string
  | {
    all?: ConditionExpression[];
    any?: ConditionExpression[];
    not?: ConditionExpression;
  };

export interface TemplateSlotEntry {
  block: string;
  props?: JsonRecord;
  slots?: Record<string, TemplateSlotList>;
  when?: ConditionExpression;
  key?: string;
  area?: string;
}

export type TemplateSlotList = TemplateSlotEntry[];

export interface CatalogueTemplateDefinition {
  id: string;
  extends?: string;
  version?: string;
  meta?: TemplateMetadata;
  regions: CatalogueTemplateRegion[];
  layout?: Record<string, LayoutOverrides>;
  slots: Record<string, TemplateSlotList>;
  /** Style for catalogue link in navbar: 'text', 'icon', or 'both'. Defaults to 'text'. */
  catalogueLinkStyle?: 'text' | 'icon' | 'both';
  /** Custom text for catalogue link when using 'text' or 'both' style. Defaults to 'Catálogo'. */
  catalogueLinkText?: string;
}


export type TemplateSchemaErrorReason =
  | 'validation-error'
  | 'unknown-block'
  | 'missing-definition'
  | 'load-error';

export class TemplateSchemaError extends Error {
  public readonly reason: TemplateSchemaErrorReason;

  public readonly issues?: ReadonlyArray<string> | null;

  public readonly ajvErrors?: ErrorObject[] | null | undefined;

  constructor(
    reason: TemplateSchemaErrorReason,
    message: string,
    options: {
      issues?: ReadonlyArray<string> | null;
      ajvErrors?: ErrorObject[] | null | undefined;
      cause?: unknown;
    } = {},
  ) {
    super(message);
    this.name = 'TemplateSchemaError';
    this.reason = reason;
    this.issues = options.issues ?? null;
    this.ajvErrors = options.ajvErrors;
    if (options.cause !== undefined) {
      (this as { cause?: unknown }).cause = options.cause;
    }
  }
}

const ajv = new Ajv2020({ allErrors: true, strict: false });

ajv.addFormat('uri', {
  type: 'string',
  validate: (value: string) => {
    try {
      new URL(value);
      return true;
    } catch (error) {
      return false;
    }
  },
});

const validateDefinition = ajv.compile<CatalogueTemplateDefinition>(schemaDefinition as any);

type TemplateDefinitionModule = { default: unknown } | unknown;

type TemplateDefinitionLoader = () => Promise<TemplateDefinitionModule>;

type MockTemplateLoader = TemplateDefinitionLoader & {
  getMockImplementation?: () => TemplateDefinitionLoader | undefined;
  mock?: {
    calls: unknown[][];
    results: Array<{ type: 'return' | 'throw'; value: unknown }>;
    instances: unknown[];
    contexts?: unknown[];
  };
};

const wrapMockLoader = (loader: TemplateDefinitionLoader): TemplateDefinitionLoader => {
  const mockLoader = loader as MockTemplateLoader;
  if (!mockLoader.mock || typeof mockLoader.getMockImplementation !== 'function') {
    return loader;
  }

  const implementation = mockLoader.getMockImplementation?.();
  if (!implementation) {
    return loader;
  }

  // 🔒 Narrow explícito
  const mock = mockLoader.mock!;

  return async () => {
    // ver fix de TS2345 más abajo
    const args: [] = [];
    const context = undefined;

    try {
      const value = await implementation.apply(context, args);
      mock.calls.push(args);
      mock.instances.push(context);
      mock.contexts?.push(context);
      mock.results.push({ type: 'return', value });
      return value;
    } catch (error) {
      mock.calls.push(args);
      mock.instances.push(context);
      mock.contexts?.push(context);
      mock.results.push({ type: 'throw', value: error });
      throw error;
    }
  };
};

const templateModules = import.meta.glob<TemplateDefinitionModule>('../definitions/*.json');

const definitionCache = new Map<string, Promise<CatalogueTemplateDefinition>>();

export interface ParseTemplateDefinitionOptions {
  source?: string;
}

export interface LoadTemplateDefinitionOptions extends ParseTemplateDefinitionOptions {
  loader?: TemplateDefinitionLoader;
  forceReload?: boolean;
}

const buildPointer = (instancePath?: string) => {
  if (!instancePath) {
    return '';
  }

  return instancePath
    .split('/')
    .filter(Boolean)
    .map((segment) => (segment.match(/^\d+$/) ? `[${segment}]` : `.${segment}`))
    .join('')
    .replace(/^\./, '');
};

const formatAjvError = (error: ErrorObject): string => {
  const pointer = buildPointer('instancePath' in error ? error.instancePath : undefined);

  if (error.keyword === 'required') {
    const missing = (error.params as { missingProperty: string }).missingProperty;
    return pointer
      ? `Falta la propiedad obligatoria "${missing}" en "${pointer}".`
      : `Falta la propiedad obligatoria "${missing}".`;
  }

  if (error.keyword === 'additionalProperties') {
    const additional = (error.params as { additionalProperty: string }).additionalProperty;
    return pointer
      ? `La propiedad "${additional}" no está permitida en "${pointer}".`
      : `La propiedad "${additional}" no está permitida.`;
  }

  if (error.keyword === 'enum') {
    const allowed = (error.params as { allowedValues: unknown[] }).allowedValues;
    return pointer
      ? `El valor en "${pointer}" debe ser uno de: ${allowed.join(', ')}.`
      : `El valor debe ser uno de: ${allowed.join(', ')}.`;
  }

  if (error.keyword === 'type') {
    const expected = (error.params as { type: string }).type;
    return pointer
      ? `El valor en "${pointer}" debe ser de tipo ${expected}.`
      : `El valor debe ser de tipo ${expected}.`;
  }

  if (error.keyword === 'minItems') {
    const limit = (error.params as { limit: number }).limit;
    return pointer
      ? `La lista en "${pointer}" debe tener al menos ${limit} elemento(s).`
      : `La lista debe tener al menos ${limit} elemento(s).`;
  }

  const message = error.message ?? 'Entrada inválida.';
  return pointer ? `${pointer}: ${message}` : message;
};

const formatAjvErrors = (
  errors: ErrorObject[] | null | undefined,
  source?: string,
): ReadonlyArray<string> => {
  if (!errors || errors.length === 0) {
    return source ? [`El archivo "${source}" no cumple el esquema.`] : ['La definición no cumple el esquema.'];
  }

  return errors.map((error) => {
    return formatAjvError(error);
  });
};

const assertBlocksExist = (definition: CatalogueTemplateDefinition, source?: string) => {
  const registry = getBlockRegistryForDesign(definition.id);
  const knownBlocks = new Set<string>();
  const issues: string[] = [];

  Object.values(registry).forEach((registrySection) => {
    Object.keys(registrySection).forEach((blockName) => {
      knownBlocks.add(blockName);
    });
  });

  const visitSlotList = (slotList: TemplateSlotList) => {
    slotList.forEach((entry) => {
      if (!knownBlocks.has(entry.block)) {
        issues.push(`El bloque "${entry.block}" no está registrado en el catálogo.`);
        return;
      }

      if (entry.slots) {
        Object.values(entry.slots).forEach((childList) => {
          visitSlotList(childList);
        });
      }
    });
  };

  Object.entries(definition.slots).forEach(([_slotName, slotList]) => {
    visitSlotList(slotList);
  });

  if (issues.length > 0) {
    const message = source ? `${source}: ${issues[0]}` : issues[0];
    throw new TemplateSchemaError('unknown-block', message, { issues });
  }
};

const parseModule = async (
  loader: TemplateDefinitionLoader,
  design: string,
  options?: ParseTemplateDefinitionOptions,
): Promise<CatalogueTemplateDefinition> => {
  let module: TemplateDefinitionModule;

  try {
    module = await loader();
  } catch (error) {
    throw new TemplateSchemaError(
      'load-error',
      `No se pudo cargar la definición JSON para "${design}": ${error instanceof Error ? error.message : String(error)}`,
      { cause: error },
    );
  }

  const candidate =
    module && typeof module === 'object' && 'default' in module ? (module as { default: unknown }).default : module;

  return parseTemplateDefinition(candidate, { source: options?.source ?? `${design}.json` });
};

export const parseTemplateDefinition = (
  raw: unknown,
  options: ParseTemplateDefinitionOptions = {},
): CatalogueTemplateDefinition => {
  const valid = validateDefinition(raw);

  if (!valid) {
    const issues = formatAjvErrors(validateDefinition.errors, options.source);
    const primaryMessage = issues[0] ?? 'La definición no es válida.';
    const message = options.source ? `${options.source}: ${primaryMessage}` : primaryMessage;
    throw new TemplateSchemaError('validation-error', message, {
      ajvErrors: validateDefinition.errors,
      issues,
    });
  }

  const definition = raw as CatalogueTemplateDefinition;
  assertBlocksExist(definition, options.source);
  return definition;
};

const getDefaultLoader = (design: string): TemplateDefinitionLoader | undefined => {
  const key = `../definitions/${design}.json`;
  const loader = templateModules[key];
  if (!loader) {
    return undefined;
  }

  return loader as TemplateDefinitionLoader;
};

export const resetTemplateDefinitionCache = () => {
  definitionCache.clear();
};

export const loadTemplateDefinition = async (
  design: string,
  options: LoadTemplateDefinitionOptions = {},
): Promise<CatalogueTemplateDefinition> => {
  if (!design) {
    throw new TemplateSchemaError('missing-definition', 'Debes especificar el diseño del template.');
  }

  const { forceReload = false } = options;

  if (!forceReload && definitionCache.has(design)) {
    return definitionCache.get(design)!;
  }

  const loader = options.loader ?? getDefaultLoader(design);

  if (!loader) {
    throw new TemplateSchemaError(
      'missing-definition',
      `No se encontró un archivo JSON para el diseño "${design}".`,
    );
  }

  const wrappedLoader = wrapMockLoader(loader);
  const promise = parseModule(wrappedLoader, design, options).catch((error) => {
    if (definitionCache.get(design) === promise) {
      definitionCache.delete(design);
    }
    throw error;
  });

  definitionCache.set(design, promise);
  return promise;
};
